'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@clerk/nextjs';
import { DateTime } from 'effect';
import { useCookieConsentStore } from './use-cookie-consent-store';
import { useCookieConsentRpc, useUpdateCookieConsentRpc, useDeleteCookieConsentRpc } from './use-cookie-consent-rpc';
import type { UseCookieConsent, CookieCategory, CategoryConsent, ConsentMethod, SyncStatus } from './types';
import { CONSENT_VERSION, DEFAULT_CATEGORY_CONSENT, ALL_CATEGORIES_ENABLED, ALWAYS_ENABLED_CATEGORIES } from './types';

/**
 * Public hook for cookie consent management
 *
 * This is the ONLY way to interact with cookie consent state.
 * It wraps the internal Zustand store and provides:
 * - Business logic for consent actions
 * - Backend synchronization when authenticated
 * - LocalStorage fallback for anonymous users
 *
 * Usage:
 * ```tsx
 * const {
 *   hasConsented,
 *   categories,
 *   acceptAll,
 *   rejectAll,
 *   hasConsentFor
 * } = useCookieConsent()
 * ```
 */
export function useCookieConsent() {
  const $ = _c(63);
  const {
    isSignedIn,
    isLoaded: authLoaded
  } = useAuth();
  const {
    hasConsented,
    status,
    categories,
    consentedAt,
    consentVersion,
    consentMethod,
    _hasSyncedFromBackend,
    setConsentState,
    reset,
    setHasSyncedFromBackend
  } = useCookieConsentStore();
  const [syncOutcome, setSyncOutcome] = useState("idle");
  const [syncError, setSyncError] = useState(null);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const isBackendAuthoritative = authLoaded && isSignedIn === true;
  let t0;
  if ($[0] !== isBackendAuthoritative) {
    t0 = {
      enabled: isBackendAuthoritative
    };
    $[0] = isBackendAuthoritative;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    consent: backendConsent,
    isLoading: isLoadingBackend,
    refetch
  } = useCookieConsentRpc(t0);
  const {
    updateConsentAsync,
    isUpdating
  } = useUpdateCookieConsentRpc();
  const {
    deleteConsentAsync
  } = useDeleteCookieConsentRpc();
  let t1;
  if ($[2] !== categories || $[3] !== consentMethod || $[4] !== isBackendAuthoritative || $[5] !== updateConsentAsync) {
    t1 = (overrideCategories, overrideMethod) => {
      if (!isBackendAuthoritative) {
        return Promise.resolve();
      }
      const categoriesToSync = overrideCategories ?? categories;
      const methodToSync = overrideMethod ?? consentMethod ?? "settings";
      return updateConsentAsync({
        essential: categoriesToSync.essential,
        performance: categoriesToSync.performance,
        analytics: categoriesToSync.analytics,
        advertising: categoriesToSync.advertising,
        socialNetworking: categoriesToSync.socialNetworking,
        unclassified: categoriesToSync.unclassified,
        consentMethod: methodToSync
      }).then(() => {
        setSyncOutcome("synced");
        setLastSyncedAt(new Date().toISOString());
        setSyncError(null);
      }, error => {
        setSyncOutcome("error");
        setSyncError(error instanceof Error ? error.message : "Sync failed");
        console.error("[CookieConsent] Backend sync failed:", error);
      });
    };
    $[2] = categories;
    $[3] = consentMethod;
    $[4] = isBackendAuthoritative;
    $[5] = updateConsentAsync;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const syncToBackend = t1;
  let t2;
  if ($[7] !== isBackendAuthoritative || $[8] !== refetch || $[9] !== setConsentState) {
    t2 = async () => {
      if (!isBackendAuthoritative) {
        return;
      }
      ;
      try {
        const result = await refetch();
        if (result.data) {
          setConsentState({
            hasConsented: true,
            status: "accepted",
            categories: {
              essential: result.data.essential,
              performance: result.data.performance,
              analytics: result.data.analytics,
              advertising: result.data.advertising,
              socialNetworking: result.data.socialNetworking,
              unclassified: result.data.unclassified
            },
            consentedAt: DateTime.formatIso(result.data.consentedAt),
            consentVersion: result.data.consentVersion,
            consentMethod: result.data.consentMethod
          });
          setSyncOutcome("synced");
          setLastSyncedAt(new Date().toISOString());
          setSyncError(null);
        } else {
          if (result.data === null) {
            setSyncOutcome("synced");
            setLastSyncedAt(new Date().toISOString());
            setSyncError(null);
          }
        }
      } catch (t3) {
        const error_0 = t3;
        setSyncOutcome("error");
        setSyncError(error_0 instanceof Error ? error_0.message : "Refresh failed");
        console.error("[CookieConsent] Backend refresh failed:", error_0);
      }
    };
    $[7] = isBackendAuthoritative;
    $[8] = refetch;
    $[9] = setConsentState;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const refreshFromBackend = t2;
  let t3;
  let t4;
  if ($[11] !== _hasSyncedFromBackend || $[12] !== backendConsent || $[13] !== categories || $[14] !== consentMethod || $[15] !== consentedAt || $[16] !== hasConsented || $[17] !== isBackendAuthoritative || $[18] !== isLoadingBackend || $[19] !== setConsentState || $[20] !== setHasSyncedFromBackend || $[21] !== syncToBackend) {
    t3 = () => {
      if (!isBackendAuthoritative || isLoadingBackend || _hasSyncedFromBackend) {
        return;
      }
      if (backendConsent === undefined) {
        return;
      }
      if (backendConsent === null) {
        if (hasConsented && consentedAt !== null) {
          syncToBackend(categories, consentMethod ?? "banner");
        }
        setHasSyncedFromBackend(true);
        return;
      }
      if (backendConsent.essential === undefined) {
        return;
      }
      const localHasMorePermissiveConsent = hasConsented && (categories.analytics && !backendConsent.analytics || categories.performance && !backendConsent.performance || categories.advertising && !backendConsent.advertising || categories.socialNetworking && !backendConsent.socialNetworking || categories.unclassified && !backendConsent.unclassified);
      const localHasExplicitConsent = hasConsented && consentedAt !== null;
      const backendHasOnlyDefaults = !backendConsent.analytics && !backendConsent.performance && !backendConsent.advertising && !backendConsent.socialNetworking && !backendConsent.unclassified;
      if (localHasMorePermissiveConsent || localHasExplicitConsent && backendHasOnlyDefaults) {
        syncToBackend(categories, consentMethod ?? "banner");
        setHasSyncedFromBackend(true);
      } else {
        setConsentState({
          hasConsented: true,
          status: "accepted",
          categories: {
            essential: backendConsent.essential,
            performance: backendConsent.performance,
            analytics: backendConsent.analytics,
            advertising: backendConsent.advertising,
            socialNetworking: backendConsent.socialNetworking,
            unclassified: backendConsent.unclassified
          },
          consentedAt: DateTime.formatIso(backendConsent.consentedAt),
          consentVersion: backendConsent.consentVersion,
          consentMethod: backendConsent.consentMethod
        });
        setHasSyncedFromBackend(true);
      }
    };
    t4 = [isBackendAuthoritative, isLoadingBackend, backendConsent, setConsentState, _hasSyncedFromBackend, setHasSyncedFromBackend, hasConsented, categories, consentedAt, consentMethod, syncToBackend];
    $[11] = _hasSyncedFromBackend;
    $[12] = backendConsent;
    $[13] = categories;
    $[14] = consentMethod;
    $[15] = consentedAt;
    $[16] = hasConsented;
    $[17] = isBackendAuthoritative;
    $[18] = isLoadingBackend;
    $[19] = setConsentState;
    $[20] = setHasSyncedFromBackend;
    $[21] = syncToBackend;
    $[22] = t3;
    $[23] = t4;
  } else {
    t3 = $[22];
    t4 = $[23];
  }
  useEffect(t3, t4);
  let t5;
  if ($[24] !== isBackendAuthoritative || $[25] !== setConsentState || $[26] !== syncToBackend) {
    t5 = () => {
      setConsentState({
        hasConsented: true,
        status: "accepted",
        categories: ALL_CATEGORIES_ENABLED,
        consentedAt: new Date().toISOString(),
        consentVersion: CONSENT_VERSION,
        consentMethod: "banner"
      });
      if (isBackendAuthoritative) {
        syncToBackend(ALL_CATEGORIES_ENABLED, "banner");
      }
    };
    $[24] = isBackendAuthoritative;
    $[25] = setConsentState;
    $[26] = syncToBackend;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  const acceptAll = t5;
  let t6;
  if ($[28] !== isBackendAuthoritative || $[29] !== setConsentState || $[30] !== syncToBackend) {
    t6 = () => {
      setConsentState({
        hasConsented: true,
        status: "rejected",
        categories: DEFAULT_CATEGORY_CONSENT,
        consentedAt: new Date().toISOString(),
        consentVersion: CONSENT_VERSION,
        consentMethod: "banner"
      });
      if (isBackendAuthoritative) {
        syncToBackend(DEFAULT_CATEGORY_CONSENT, "banner");
      }
    };
    $[28] = isBackendAuthoritative;
    $[29] = setConsentState;
    $[30] = syncToBackend;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  const rejectAll = t6;
  let t7;
  if ($[32] !== categories || $[33] !== isBackendAuthoritative || $[34] !== setConsentState || $[35] !== syncToBackend) {
    t7 = (newCategories, t8) => {
      const method = t8 === undefined ? "settings" : t8;
      const mergedCategories = {
        ...categories,
        ...newCategories,
        essential: true
      };
      setConsentState({
        hasConsented: true,
        status: "accepted",
        categories: mergedCategories,
        consentedAt: new Date().toISOString(),
        consentVersion: CONSENT_VERSION,
        consentMethod: method
      });
      if (isBackendAuthoritative) {
        syncToBackend(mergedCategories, method);
      }
    };
    $[32] = categories;
    $[33] = isBackendAuthoritative;
    $[34] = setConsentState;
    $[35] = syncToBackend;
    $[36] = t7;
  } else {
    t7 = $[36];
  }
  const acceptCategories = t7;
  let t8;
  if ($[37] !== deleteConsentAsync || $[38] !== isBackendAuthoritative || $[39] !== reset || $[40] !== updateConsentAsync) {
    t8 = async () => {
      reset();
      if (isBackendAuthoritative) {
        ;
        try {
          await deleteConsentAsync(undefined);
        } catch (t9) {
          const error_1 = t9;
          console.error("[CookieConsent] Failed to delete backend consent:", error_1);
          ;
          try {
            await updateConsentAsync({
              essential: true,
              performance: false,
              analytics: false,
              advertising: false,
              socialNetworking: false,
              unclassified: false,
              consentMethod: "settings"
            });
          } catch (t10) {
            const fallbackError = t10;
            console.error("[CookieConsent] Failed to reset backend consent:", fallbackError);
          }
        }
      }
    };
    $[37] = deleteConsentAsync;
    $[38] = isBackendAuthoritative;
    $[39] = reset;
    $[40] = updateConsentAsync;
    $[41] = t8;
  } else {
    t8 = $[41];
  }
  const resetConsent = t8;
  let t9;
  if ($[42] !== categories) {
    t9 = category => {
      if (ALWAYS_ENABLED_CATEGORIES.includes(category)) {
        return true;
      }
      return categories[category] ?? false;
    };
    $[42] = categories;
    $[43] = t9;
  } else {
    t9 = $[43];
  }
  const hasConsentFor = t9;
  const syncStatus = isUpdating ? "syncing" : syncOutcome === "idle" && isBackendAuthoritative && _hasSyncedFromBackend ? "synced" : syncOutcome;
  let t10;
  if ($[44] !== _hasSyncedFromBackend || $[45] !== acceptAll || $[46] !== acceptCategories || $[47] !== categories || $[48] !== consentMethod || $[49] !== consentVersion || $[50] !== consentedAt || $[51] !== hasConsentFor || $[52] !== hasConsented || $[53] !== isBackendAuthoritative || $[54] !== lastSyncedAt || $[55] !== refreshFromBackend || $[56] !== rejectAll || $[57] !== resetConsent || $[58] !== status || $[59] !== syncError || $[60] !== syncStatus || $[61] !== syncToBackend) {
    t10 = {
      hasConsented,
      status,
      categories,
      consentedAt,
      consentVersion,
      consentMethod,
      _hasSyncedFromBackend,
      syncStatus,
      syncError,
      lastSyncedAt,
      isBackendAuthoritative,
      acceptAll,
      rejectAll,
      acceptCategories,
      resetConsent,
      hasConsentFor,
      syncToBackend,
      refreshFromBackend
    };
    $[44] = _hasSyncedFromBackend;
    $[45] = acceptAll;
    $[46] = acceptCategories;
    $[47] = categories;
    $[48] = consentMethod;
    $[49] = consentVersion;
    $[50] = consentedAt;
    $[51] = hasConsentFor;
    $[52] = hasConsented;
    $[53] = isBackendAuthoritative;
    $[54] = lastSyncedAt;
    $[55] = refreshFromBackend;
    $[56] = rejectAll;
    $[57] = resetConsent;
    $[58] = status;
    $[59] = syncError;
    $[60] = syncStatus;
    $[61] = syncToBackend;
    $[62] = t10;
  } else {
    t10 = $[62];
  }
  return t10;
}

// Re-export types for convenience
export type { CookieCategory, ConsentStatus, CategoryConsent } from './types';